import {
    SmsAccount,
    SmsAccountSubscription,
    SxmTypeBillingStatus,
    SxmTypeBillingStatusDateAmount,
    SxmTypePaymentType,
} from '@sxm-de/dev-kits/web-sdk/types';
import {
    BillingSummary,
    Quote,
} from '@sxm-de/dev-kits/microservices-sdk/types';
import { parseDateOptional } from '../parse-date';
import { getDaysFromToday } from '../get-days-from-today';

/**
 * TODO:
 * verify mapping logic with existing logic
 * de-care-use-cases/account/state-common/src/lib/state/selectors.ts
 * start @ line 182
 */
export function mapSmsBillingSummaryToBillingStatus(
    smsAccount: SmsAccount,
    currentQuote: Quote | null,
): SxmTypeBillingStatus | null {
    const billingSummary = smsAccount?.billingSummary;

    if (!billingSummary) {
        return null;
    }

    const isPastDue = billingSummary.isAccountInCollection === true;

    const amountDue = getAmountDue(billingSummary, currentQuote);

    const type = getAccountType(smsAccount, isPastDue, amountDue);

    const billingStatus: SxmTypeBillingStatus = {
        amountDue,
        isInvoice: billingSummary.isPaymentTypeInvoice === true,
        isPastDue,
        lastPayment: getDateAmount(
            billingSummary.lastPaymentDate,
            billingSummary.lastPaymentAmount,
        ),
        nextPayment: getDateAmount(
            billingSummary.nextPaymentDate,
            billingSummary.amountDue,
        ),
        paymentType: (billingSummary.paymentType as SxmTypePaymentType) ?? null,
    };

    return billingStatus;
}

function getAmountDue(
    billingSummary: BillingSummary,
    currentQuote: Quote | null,
): number {
    if (currentQuote) {
        /**
         * TODO:
         * check quote mapping:
         * totalAmount is likely a number, not string
         */
        return parseFloat(currentQuote.totalAmount);
    }

    if (billingSummary.amountDue && billingSummary.amountDue > 0) {
        return billingSummary.amountDue;
    }

    if (billingSummary?.amountDue! + billingSummary?.nextPaymentAmount! > 0) {
        return billingSummary?.amountDue! + billingSummary?.nextPaymentAmount!;
    }

    return 0;

    // existing logic:
    // return billingSummary?.amountDue! > 0
    //     ? billingSummary.amountDue!
    //     : billingSummary?.amountDue! + billingSummary?.nextPaymentAmount! > 0
    //       ? billingSummary?.amountDue! + billingSummary?.nextPaymentAmount!
    //       : 0;
}

function getDateAmount(
    date: string | undefined,
    amount: number | undefined,
): SxmTypeBillingStatusDateAmount {
    const d = parseDateOptional(date);
    const diff = d ? getDaysFromToday(d) : null;
    return {
        date: d,
        amount: amount ?? null,
        offsetDaysFromToday: diff,
    };
}
function getAccountType(
    smsAccount: SmsAccount,
    isPastDue: boolean,
    amountDue: number,
) {
    const billingSummary = smsAccount.billingSummary;
    const subscriptions = smsAccount.subscriptions;
    if (
        (onlyTrialPlans(subscriptions) ||
            onlyExtendendServicePlans(subscriptions)) &&
        !isPastDue &&
        amountDue === 0
    ) {
        if (anyTrialPlansWithFollowon(subscriptions)) {
            // type is BILLING_WITH_TRIALER_NO_PAYMENT_DUE_WITH_FOLLOWON
        } else if (hasLifetimeTrialsOnly(subscriptions)) {
            // type is BILLING_WITH_LIFETIME_TRIALER_NO_PAYMENT_DUE
        } else {
            // type is BILLING_WITH_TRIALER_NO_PAYMENT_DUE
        }
    } else {
        if (isPastDue) {
            // type is BILLING_WITH_MAKE_PAYMENT;
        } else {
            if (billingSummary.paymentType === 'invoice') {
                if (amountDue === 0) {
                    // type is BILLING_WITH_NO_PAYMENT_DUE
                } else {
                    // type is BILLING_WITH_MAKE_PAYMENT
                }
            } else {
                if (amountDue === 0) {
                    // type is BILLING_WITH_NO_PAYMENT_DUE_AUTOMATED
                } else {
                    // type is BILLING_WITH_AUTOMATED_PAYMENT
                }
            }
        }
    }
}

function onlyTrialPlans(subscriptions: SmsAccountSubscription[]): boolean {
    return (
        subscriptions.length > 0 &&
        subscriptions.every((sub) =>
            sub.plans?.every((plan) => plan.type.toLowerCase() === 'trial'),
        )
    );
}

function onlyExtendendServicePlans(
    subscriptions: SmsAccountSubscription[],
): boolean {
    return (
        subscriptions.length > 0 &&
        subscriptions.every((sub) =>
            sub.plans?.every(
                (plan) => plan.type.toLowerCase() === 'extended-service',
            ),
        )
    );
}

function anyTrialPlansWithFollowon(
    subscriptions: SmsAccountSubscription[],
): boolean {
    return (
        subscriptions?.length > 0 &&
        subscriptions.some(
            (sub) =>
                sub.plans?.every(
                    (plan) => plan.type.toLowerCase() === 'trial',
                ) && sub.followonPlans?.length > 0,
        )
    );
}

function hasLifetimeTrialsOnly(
    subscriptions: SmsAccountSubscription[],
): boolean {
    return subscriptions?.every((sub) =>
        sub.plans.every(
            (plan) => plan.type.toLowerCase() === 'trial' && plan.isLifetime,
        ),
    );
}
